import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Users, Send, ListChecks, TrendingUp, QrCode, Images,
  Link2, MessageSquare, Bot, CalendarClock, Check, ArrowRight, ScanLine,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import Navbar from "@/components/xuma/Navbar";
import Footer from "@/components/xuma/Footer";
import FlipCard from "@/components/xuma/FlipCard";
import CheckoutDialog from "@/components/xuma/CheckoutDialog";
import WaitlistDialog from "@/components/xuma/WaitlistDialog";

const pillars = [
  { icon: Users, title: "Presencia", text: "Muestra quién eres, qué haces y por qué deberían elegirte." },
  { icon: Send, title: "Conexión", text: "Que te contacten, compartan o recomienden en un solo toque." },
  { icon: TrendingUp, title: "Conversión", text: "Convierte visitas en prospectos con formularios y llamadas." },
  { icon: ListChecks, title: "Seguimiento", text: "Organiza tus contactos para que ninguna oportunidad se pierda." },
];

const basicFeatures = [
  "Una tarjeta digital", "Diseño con frente y reverso", "Animación de giro",
  "Código QR", "Cinco fotografías", "Cinco enlaces", "Redes sociales",
  "Formulario de contacto", "Mini CRM", "Estadísticas básicas",
  "Descarga de contacto", "Soporte estándar",
];

const premiumFeatures = [
  "Todo lo del plan Básico", "Asistente inteligente", "Instrucciones personalizables",
  "Base de conocimiento", "Captura automática de prospectos", "Conexión con CRM",
  "Agenda interna", "Calendario externo opcional", "Recordatorios",
  "Historial de conversaciones", "Estadísticas avanzadas", "Hasta 100 conversaciones mensuales",
];

const faqs = [
  { q: "¿Necesito instalar algo?", a: "No. La tarjeta se abre desde el navegador mediante enlace o código QR." },
  { q: "¿Puedo cambiar mi información?", a: "Sí. Podrás editar contenido, colores, enlaces, redes y fotografías desde tu panel." },
  { q: "¿Qué sucede con los contactos del formulario?", a: "Se almacenan en tu mini CRM para que puedas revisar y dar seguimiento." },
  { q: "¿El código QR cambia?", a: "El código dirige a tu tarjeta. Puedes actualizar el contenido sin necesidad de imprimir un código nuevo." },
  { q: "¿El asistente puede agendar citas?", a: "Sí, en el plan premium puede consultar disponibilidad y registrar citas." },
  { q: "¿Qué pasa si supero el límite del asistente?", a: "Se te notificará antes de llegar al límite y podrás adquirir capacidad adicional o esperar al siguiente periodo." },
];

const Feature = ({ icon: Icon, children }) => (
  <li className="flex items-start gap-3 text-sm">
    <Check className="h-4 w-4 text-xuma-primary mt-0.5 shrink-0" />
    <span>{children}</span>
  </li>
);

export default function Landing() {
  const [checkout, setCheckout] = useState({ open: false, plan: "basic" });
  const openCheckout = (plan) => setCheckout({ open: true, plan });
  const [waitlist, setWaitlist] = useState({ open: false, plan: "premium" });
  const openWaitlist = (plan) => setWaitlist({ open: true, plan });
  return (
    <div className="App bg-xuma-bg">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-[0.06] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-5 pt-16 pb-20 md:pt-24 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-white border border-xuma-border px-4 py-1.5 text-xs font-medium text-xuma-muted">
              <Sparkles className="h-3.5 w-3.5 text-xuma-primary" /> Cada contacto suma
            </span>
            <h1 className="mt-6 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-xuma-dark">
              Cada contacto puede convertirse en una{" "}
              <span className="text-xuma-primary">oportunidad</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-xuma-muted max-w-lg leading-relaxed">
              Crea una tarjeta digital interactiva, comparte tu negocio y recibe nuevos
              prospectos desde un solo lugar. Xuma Social Business Card te ayuda a presentar, conectar y crecer.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register">
                <Button data-testid="hero-cta-primary" className="bg-xuma-primary hover:bg-xuma-primaryHover text-white rounded-full px-7 h-12 text-base hover:-translate-y-0.5 transition-transform">
                  Crear mi tarjeta digital <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <a href="#pricing">
                <Button data-testid="hero-cta-pricing" variant="outline" className="rounded-full px-7 h-12 text-base border-xuma-dark/20 text-xuma-dark hover:bg-white">
                  Ver planes
                </Button>
              </a>
            </div>
            <p className="mt-6 text-sm text-xuma-muted flex items-center gap-2">
              <ScanLine className="h-4 w-4 text-xuma-primary" /> Se abre desde el navegador. Sin instalar nada.
            </p>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-6 rounded-[2rem] bg-xuma-accent/20 blur-2xl -z-10" />
              <FlipCard
                name="María Fernanda"
                role="Consultora de Negocios"
                slogan="Cada contacto suma"
                phone="+52 55 1234 5678"
                qrData="https://xuma.mx/maria"
              />
              <p className="text-center mt-4 text-sm text-xuma-muted">
                Toca la tarjeta para girarla y ver el QR
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section id="features" className="max-w-6xl mx-auto px-5 py-20">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-xuma-primary font-medium">Por qué Xuma Social Business Card</p>
          <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl text-xuma-dark tracking-tight">
            Mucho más que una tarjeta digital
          </h2>
          <p className="mt-4 text-xuma-muted">
            Una herramienta para sumar presencia, conexiones, oportunidades y clientes.
          </p>
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {pillars.map((p) => (
            <div key={p.title} className="rounded-2xl bg-white border border-xuma-border p-6 hover:-translate-y-1 transition-transform">
              <span className="h-11 w-11 rounded-xl bg-xuma-primary/10 grid place-items-center">
                <p.icon className="h-5 w-5 text-xuma-primary" />
              </span>
              <h3 className="mt-5 font-heading font-bold text-lg text-xuma-dark">{p.title}</h3>
              <p className="mt-2 text-sm text-xuma-muted leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>

        {/* mini feature strip */}
        <div className="mt-8 grid sm:grid-cols-3 gap-5">
          {[
            { icon: QrCode, t: "Código QR", d: "Comparte tu tarjeta al instante escaneando." },
            { icon: Images, t: "5 fotografías + 5 enlaces", d: "Presenta tu trabajo y todos tus canales." },
            { icon: Link2, t: "Redes y WhatsApp", d: "Que te contacten por su canal favorito." },
          ].map((f) => (
            <div key={f.t} className="rounded-2xl bg-xuma-dark text-white p-6">
              <f.icon className="h-6 w-6 text-xuma-accent" />
              <h4 className="mt-4 font-heading font-bold">{f.t}</h4>
              <p className="mt-1.5 text-sm text-white/60">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-white border-y border-xuma-border">
        <div className="max-w-6xl mx-auto px-5 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-xs uppercase tracking-[0.2em] text-xuma-primary font-medium">Planes</p>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl text-xuma-dark tracking-tight">
              Elige cómo quieres crecer
            </h2>
            <p className="mt-4 text-xuma-muted">Precios en pesos mexicanos (MXN).</p>
          </div>

          <div className="mt-14 grid lg:grid-cols-3 gap-6 items-start">
            {/* Básico */}
            <div className="rounded-3xl border border-xuma-border p-8 bg-xuma-bg">
              <h3 className="font-heading font-extrabold text-2xl text-xuma-dark">Básico</h3>
              <div className="mt-4">
                <p className="text-sm text-xuma-muted">$699 MXN de configuración</p>
                <p className="mt-1"><span className="font-heading font-extrabold text-4xl text-xuma-dark">$99</span><span className="text-xuma-muted"> MXN / mes</span></p>
              </div>
              <ul className="mt-6 space-y-2.5 text-xuma-text">
                {basicFeatures.map((f) => <Feature key={f}>{f}</Feature>)}
              </ul>
              <Button data-testid="pricing-basic-cta" onClick={() => openCheckout("basic")} className="w-full mt-8 rounded-full h-12 bg-xuma-dark hover:bg-black text-white">
                Crear tarjeta básica
              </Button>
            </div>

            {/* Premium (highlighted) */}
            <div className="rounded-3xl border-2 border-xuma-primary p-8 bg-white relative shadow-[0_30px_60px_-25px_rgba(226,90,56,0.4)] lg:-translate-y-3">
              <span className="absolute -top-3 left-8 rounded-full bg-xuma-primary text-white text-xs font-medium px-3 py-1 flex items-center gap-1">
                <Bot className="h-3.5 w-3.5" /> Recomendado
              </span>
              <h3 className="font-heading font-extrabold text-2xl text-xuma-dark">Premium Inteligente</h3>
              <div className="mt-4">
                <p className="text-sm text-xuma-muted">$1,499 MXN de configuración</p>
                <p className="mt-1"><span className="font-heading font-extrabold text-4xl text-xuma-primary">$299</span><span className="text-xuma-muted"> MXN / mes</span></p>
              </div>
              <ul className="mt-6 space-y-2.5 text-xuma-text">
                {premiumFeatures.map((f) => <Feature key={f}>{f}</Feature>)}
              </ul>
              <Button data-testid="pricing-premium-cta" onClick={() => openWaitlist("premium")} className="w-full mt-8 rounded-full h-12 bg-xuma-primary hover:bg-xuma-primaryHover text-white">
                Unirme a la lista de espera
              </Button>
            </div>

            {/* Fundadores */}
            <div className="rounded-3xl p-8 bg-xuma-dark text-white relative overflow-hidden">
              <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-xuma-accent/20 blur-2xl" />
              <span className="inline-flex items-center gap-1 rounded-full bg-xuma-accent/20 text-xuma-accent text-xs font-medium px-3 py-1">
                <Sparkles className="h-3.5 w-3.5" /> Oferta fundadores
              </span>
              <h3 className="mt-4 font-heading font-extrabold text-2xl">Precio de lanzamiento</h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">
                Los primeros clientes acceden a una tarifa preferencial mientras mantengan activa su suscripción.
              </p>
              <div className="mt-6 space-y-4">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <p className="font-heading font-bold">Básico</p>
                  <p className="text-sm text-white/70">$499 config · $59 / mes</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-4">
                  <p className="font-heading font-bold">Premium</p>
                  <p className="text-sm text-white/70">$999 config · $199 / mes</p>
                </div>
              </div>
              <Button data-testid="pricing-founder-cta" onClick={() => openWaitlist("founder_premium")} className="w-full mt-8 rounded-full h-12 bg-xuma-accent hover:bg-xuma-accent/90 text-xuma-dark font-semibold">
                Quiero precio fundador
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* PREMIUM SPOTLIGHT */}
      <section className="max-w-6xl mx-auto px-5 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-xuma-primary font-medium">Premium Inteligente</p>
          <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl text-xuma-dark tracking-tight">
            Un asistente que atiende y agenda por ti
          </h2>
          <p className="mt-4 text-xuma-muted leading-relaxed">
            Responde preguntas con base en tus instrucciones, captura prospectos automáticamente
            y agenda citas conectándose a tu CRM y tu calendario.
          </p>
          <ul className="mt-6 space-y-3">
            <li className="flex items-center gap-3"><Bot className="h-5 w-5 text-xuma-primary" /> Asistente configurable con tu propio prompt</li>
            <li className="flex items-center gap-3"><CalendarClock className="h-5 w-5 text-xuma-primary" /> Agenda interna + Google Calendar</li>
            <li className="flex items-center gap-3"><MessageSquare className="h-5 w-5 text-xuma-primary" /> Historial de conversaciones y recordatorios</li>
          </ul>
        </div>
        <div className="rounded-3xl bg-xuma-dash border border-xuma-border p-8">
          <div className="rounded-2xl bg-white border border-xuma-border p-5 space-y-3">
            <div className="flex gap-2"><span className="h-8 w-8 rounded-full bg-xuma-primary grid place-items-center text-white text-sm"><Bot className="h-4 w-4" /></span>
              <p className="text-sm bg-xuma-bg rounded-2xl rounded-tl-sm px-4 py-2">¡Hola! Soy el asistente de María. ¿En qué puedo ayudarte hoy?</p>
            </div>
            <div className="flex gap-2 justify-end"><p className="text-sm bg-xuma-primary text-white rounded-2xl rounded-tr-sm px-4 py-2">Quiero agendar una consultoría</p></div>
            <div className="flex gap-2"><span className="h-8 w-8 rounded-full bg-xuma-primary grid place-items-center text-white text-sm"><Bot className="h-4 w-4" /></span>
              <p className="text-sm bg-xuma-bg rounded-2xl rounded-tl-sm px-4 py-2">Perfecto. Tengo disponibilidad el martes 10:00 o jueves 16:00. ¿Cuál prefieres?</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-white border-y border-xuma-border">
        <div className="max-w-3xl mx-auto px-5 py-20">
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-xuma-dark tracking-tight text-center">
            Preguntas frecuentes
          </h2>
          <Accordion type="single" collapsible className="mt-10">
            {faqs.map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-heading font-bold text-xuma-dark hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-xuma-muted leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-xuma-dark relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-[0.08] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-5 py-24 md:py-32 text-center relative">
          <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-white tracking-tight leading-tight">
            Tu próximo cliente puede encontrarte con un solo escaneo
          </h2>
          <p className="mt-5 text-white/60 max-w-xl mx-auto">
            Crea una presencia profesional, comparte tu negocio y convierte visitas en oportunidades.
          </p>
          <Link to="/register" className="inline-block mt-8">
            <Button data-testid="final-cta-btn" className="bg-xuma-primary hover:bg-xuma-primaryHover text-white rounded-full px-8 h-13 py-6 text-base hover:-translate-y-0.5 transition-transform">
              Crear mi tarjeta digital <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
          <p className="mt-6 font-heading text-lg text-white/80">Xuma Social Business Card — Cada contacto suma.</p>
        </div>
      </section>

      <Footer />
      <CheckoutDialog open={checkout.open} initialPlan={checkout.plan}
        onOpenChange={(o) => setCheckout((c) => ({ ...c, open: o }))} />
      <WaitlistDialog open={waitlist.open} plan={waitlist.plan}
        onOpenChange={(o) => setWaitlist((w) => ({ ...w, open: o }))} />
    </div>
  );
}
